import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X, XCircle, type LucideIcon } from 'lucide-react';

type ToastTone = 'success' | 'error' | 'info' | 'warning';
interface ToastItem { id: number; tone: ToastTone; title?: string; message: string; }
interface ToastOptions { title?: string; duration?: number; }
interface ToastContextValue {
  push: (tone: ToastTone, message: string, options?: ToastOptions) => void;
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  info: (message: string, options?: ToastOptions) => void;
  warning: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const config: Record<ToastTone, { icon: LucideIcon; color: string }> = {
  success: { icon: CheckCircle2, color: 'var(--pill-completed-dot)' },
  error: { icon: XCircle, color: 'var(--pill-cancelled-dot)' },
  info: { icon: Info, color: 'var(--accent)' },
  warning: { icon: AlertTriangle, color: 'var(--booking-ink)' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);
  const dismiss = useCallback((id: number) => setToasts((c) => c.filter((t) => t.id !== id)), []);
  const push = useCallback((tone: ToastTone, message: string, options?: ToastOptions) => {
    const id = (idRef.current += 1);
    setToasts((c) => [...c, { id, tone, message, title: options?.title }]);
    window.setTimeout(() => dismiss(id), options?.duration ?? (tone === 'error' ? 7000 : 4500));
  }, [dismiss]);

  const value = useMemo<ToastContextValue>(() => ({
    push,
    success: (m, o) => push('success', m, o),
    error: (m, o) => push('error', m, o),
    info: (m, o) => push('info', m, o),
    warning: (m, o) => push('warning', m, o),
  }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed inset-x-0 top-4 z-[60] flex flex-col items-center gap-2 px-4 sm:items-end sm:pr-6">
          {toasts.map((t) => {
            const { icon: Icon, color } = config[t.tone];
            return (
              <div
                key={t.id}
                role={t.tone === 'error' ? 'alert' : 'status'}
                className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-[14px] border border-hair bg-elev px-4 py-3.5 text-ink shadow-pop animate-toast-in"
              >
                <Icon size={18} className="mt-0.5 shrink-0" style={{ color }} />
                <div className="min-w-0 flex-1 text-[14.5px] leading-snug">
                  {t.title && <div className="font-semibold text-ink">{t.title}</div>}
                  <div className="text-ink2">{t.message}</div>
                </div>
                <button type="button" onClick={() => dismiss(t.id)} aria-label="Dismiss" className="-mr-1 flex h-7 w-7 items-center justify-center rounded-[8px] text-ink3 hover:bg-hover hover:text-ink">
                  <X size={15} />
                </button>
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
